/**
 * What the room answered, per planner block (US-MB-6 fatia 2, AC3).
 *
 * Pure, because `klapzo-web` has no jsdom: everything a reviewer cannot check by reading lives here
 * and is asserted in `planner-results.test.ts`; the panel that maps these rows into JSX is a
 * DOM-only survivor, declared as such — the same discipline W10a/W10b/W11 and fatia 1 recorded.
 *
 * The DATA comes from the existing `GET /sessions/:id/results` (`api.results`, the same typed client
 * the Presenter's results screen uses) with the host's own cookie — no kit-api hop, no new route on
 * either Worker (constitution #7(b)), and retention/plan/k-anonymity stay live-api's, applied
 * server-side. Nothing new is persisted: this is a second SURFACE over rows `session_rounds` already
 * holds.
 *
 * ONE of those rows carries a nickname: a `promptvote` round opened with `revealAuthors: true`
 * persists `authors: "named"` with the author beside each prompt. A `named` OPEN panel does not —
 * live-api's `stripAuthors` drops the author at `emitClosedRound`, before D1 — so the only
 * nickname-to-text pairing this panel can show is the promptvote one, which is what constitution #5
 * is flagged about in the PR (round-1 review, M2: the earlier note claimed both).
 *
 * The JOIN is `roundsForBlock` in `@klapzo/kit-core`, beside the id rule the store validates, so the
 * planner and kit-api's cross-app proof read the same function.
 */
import { closesOn, formatMean, labelOf, meanOf, revealRows, type QuestionAggregate, type RoundResult } from "../api.js";
import { getLocale, t } from "../i18n.js";
import type { LiveSession } from "../agendaStore.js";
import type { RoundSpec } from "@klapzo/kit-core";

/** Why the "Resultados" button is dead, or `ready`. Ordered like `runState`, for the same reason:
 *  signed out, the method list is unreadable too, so any other cause would name the wrong fix. */
export type ResultsState = "ready" | "signedOut" | "noSession" | "notRecorded";

export function resultsState(spec: RoundSpec | null | undefined, live: LiveSession | null, signedIn: boolean): ResultsState {
  if (!signedIn) return "signedOut";
  if (!live) return "noSession";
  // A picker's draws are never written to `session_rounds` (the schema's CHECK excludes the type),
  // so this is a fixed ceiling to state, not a round that has not happened yet.
  if (spec?.type === "picker") return "notRecorded";
  return "ready";
}

/**
 * An option's own label, from the card that opened the round.
 *
 * The aggregate keys counts by OPTION ID (`o3`), which is what the projection renders too — but the
 * planner has the card, so it can say "Muito baixa" instead. Falls back to the id: an option the
 * card no longer lists (the spec was edited after the round ran) must still show its count rather
 * than vanish or render as blank.
 */
export function optionLabel(spec: RoundSpec | null | undefined, optionId: string): string {
  const options = spec?.config?.["options"];
  if (!Array.isArray(options)) return optionId;
  for (const o of options) {
    if (o !== null && typeof o === "object" && (o as { id?: unknown }).id === optionId) {
      const label = (o as { label?: unknown }).label;
      if (typeof label === "string" && label !== "") return label;
    }
  }
  return optionId;
}

/** One line of a round's result. `value` is already a string: the unit rides with the number
 *  (points, not votes, on a weighted round) so a row cannot be relabelled by the renderer. */
export interface ResultRow {
  label: string;
  value: string;
}

/**
 * The persisted `aggregate`, as rows — one arm per type it can be.
 *
 * The union is live-api's, hand-mirrored in this app's `api.ts` (as `QuestionConfig` already was);
 * nothing is imported from the other Worker at runtime. `promptvote` goes through the shared
 * `revealRows`, which is where the `authors: "hidden"` narrowing lives, so an anonymous round cannot
 * be de-anonymised by a second copy of that check drifting from the first.
 */
export function rowsFor(round: RoundResult, spec: RoundSpec | null | undefined): ResultRow[] {
  const agg: QuestionAggregate = round.aggregate;
  if (agg.type === "mc") {
    // A round that withheld its tally live still recorded the real counts — withholding was a
    // projection concern and the round is over — but the card asked for it, so it is said.
    if (agg.tally === "hidden") return [{ label: t("live.hiddenTally"), value: String(agg.total) }];
    const unit = agg.payload === undefined ? "" : ` ${t("prio.unit")}`;
    const rows = Object.entries(agg.counts).map(([optionId, count]) => ({
      // The RECORD first, the card second (W9b): the closed round now persists the option table it
      // ran with, and that is the label the room actually saw — a card edited after the round would
      // otherwise relabel a result. `optionLabel` stays the fallback for a round closed before this
      // wave, and the id is its own last resort.
      label: labelOf(agg.options, optionId) ?? optionLabel(spec, optionId),
      value: `${count}${unit}`,
    }));
    // The headline of a scale round, as its own trailing row — the number the room quoted.
    const mean = agg.aggregate === "mean" ? meanOf(agg.counts, agg.options, agg.total) : null;
    if (mean !== null) rows.push({ label: t("scale.meanLabel"), value: formatMean(mean, getLocale()) });
    return rows;
  }
  if (agg.type === "wordcloud") return agg.words.map((w) => ({ label: w.word, value: String(w.count) }));
  if (agg.type === "open") {
    return agg.entries.map((e) => ({
      // In a PERSISTED row the author is always absent: `stripAuthors` removes it on the way to
      // D1, so a `named` panel (US-LV-20) reaches this panel anonymous like every other. The
      // branch stays because the union is live-api's own and describes the live frame too — and
      // because the day fatia 2 of LV-20 persists attribution, this renders it instead of dropping
      // it silently.
      label: e.author === undefined ? e.text : `${e.text} — ${e.author}`,
      value: String(e.upvotes),
    }));
  }
  if (agg.type === "promptvote") {
    // Only a terminal close writes a row, so a `write`/`vote` phase cannot be persisted — handled
    // rather than cast away, because the type says it can arrive.
    if (agg.phase !== "reveal") return [];
    return revealRows(agg).map((r) => ({
      label: r.author === null ? r.text : `${r.text} — ${r.author}`,
      value: String(r.votes),
    }));
  }
  // picker: never persisted (`session_rounds`' own CHECK), and `RoundResult.type` cannot be it.
  return [];
}

/**
 * A round's one-line header: when it closed, how many answered, how long it was open.
 *
 * The instant is formatted by the SAME `closesOn` the participant's retention line uses — one
 * `Intl` call in this app, and `es-LATAM` resolves through it exactly as it does there.
 */
export function roundMeta(round: RoundResult, locale: string): string {
  const at = closesOn(round.closedAt * 1_000, locale);
  const parts: string[] = at === null ? [] : [at];
  // A part whose number is not finite is DROPPED, the same policy `closesOn` already applies to the
  // instant. These three numbers come off the wire: a field the server omitted is `undefined`, and
  // `undefined - undefined` is NaN, which would print the literal "NaN respostas · NaN min" over
  // results that are otherwise correct (round-1 review, LOW).
  const n = round.responses;
  if (Number.isFinite(n)) parts.push(`${n} ${t(n === 1 ? "results.responseOne" : "results.responses")}`);
  const openFor = round.closedAt - round.openedAt;
  // Rounded UP to a minute: a 40-second round is not "0 min".
  if (Number.isFinite(openFor)) parts.push(`${Math.max(1, Math.ceil(Math.max(0, openFor) / 60))} ${t("planner.min")}`);
  return parts.join(" · ");
}
